<template>
    <blank-layout style="overflow: hidden;">
        <div class="tree-data-grid"
            :class="{'is-not-desktop': !isDesktop}"
            style="height:100%;">
            <div class="tree-data-grid-aside" :class="{collapse}" style="height:100%background:#fff;" v-loading="loading">
                <ul class="tree-data-grid-aside-toolbar" v-if="toolbar.length">
                    <li v-for="tool in toolbar"
                        class="toolbar-item"
                        :key="tool.command"
                        v-show="_toolbarItemShow(tool.show)"
                        @click="onToolbarItemClick(tool)">
                        <common-icon :type="getToolbarItemIcon(tool)" :size="14"></common-icon>
                        <span class="toolbar-item-label">{{getToolbarItemName(tool)}}</span>
                    </li>
                </ul>
                <v-tree :data="treeData"
                    ref="tree"
                    :node-key="nodeKey"
                    :default-expanded-keys="defaultExpandedKeys"
                    :action-sheet="actionSheet"
                    :props="treeOptionNames"
                    :style="{height: treeHeight}"
                    @current-change="onCurrentNodeChange"
                    @node-drop-success="onNodeDropSuccess"/>
            </div>
            <div class="tree-data-grid-content" :class="{full: collapse}">
                <div v-if="isDesktop" class="tree-data-grid-content__trigger" @click="onToggleClick">
                    <i class="el-icon-arrow-left icon"></i>
                </div>
                <div class="tree-data-grid-content__table">
                    <data-grid ref="dataGrid"
                        :api="api"
                        v-model="model"
                        :items="items"
                        :params="params"
                        :auto-search="false"
                        :columns="columns"
                        default-loading
                        :enableToolbar="enableToolbar"
                        @on-sort-change="onSortChange">
                        <template slot="operator"><slot name="operator"/></template>
                        <slot/>
                        <template slot="dialog"><slot name="dialog"/></template>
                    </data-grid>
                </div>
            </div>
        </div>
    </blank-layout>
</template>

<script>
import VTree from '@fw/tree'
import BlankLayout from '@fw/layout/blank-layout'
import DataGrid from '@fw/data-grid/data-grid.vue'
import CommonIcon from '@fw/widgets/common-icon'
import DataGridMinxins from './data-grid-mixins'
import ResponsiveMixins from '../mixins/responsive-observer'

import { mapGetters } from 'vuex'

export default {
  name: 'v-tree-data-grid',
  mixins: [DataGridMinxins, ResponsiveMixins],
  computed: {
    ...mapGetters([
      'curNodex'
    ]),
    treeHeight () {
      if (this.toolbar.length) {
        return 'calc(100% - 30px)'
      } else {
        return '100%'
      }
    }
  },
  components: {
    VTree,
    BlankLayout,
    DataGrid,
    CommonIcon,
    RenderFunc: {
      functional: true,
      props: {
        render: {
          type: Function
        },
        scope: {
          type: Object
        }
      },
      render (h, ctx) {
        let { render, scope } = ctx.props
        return render(h, scope)
      }
    }
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler (val, oldVal) {
        if (val !== oldVal) this.model = val
      }
    },
    model: {
      deep: true,
      handler (val, oldVal) {
        if (val !== oldVal) this.$emit('input', val)
      }
    }
  },
  props: {
    nodeKey: {
      type: String,
      default: 'id'
    },
    treeApi: { // 左侧树接口
      type: String,
      required: true
    },
    treeApiParams: {
      type: Object
    },
    treeOptionNames: {
      type: Object,
      default: () => {
        return {
          label: 'label',
          children: 'children',
          disabled: 'disabled',
          isLeaf: 'isLeaf'
        }
      }
    },
    toolbar: {
      type: Array,
      default: () => []
    },
    actionSheet: {
      type: Array,
      default: () => []
    },
    columns: { // id label align header-align width render type prop
      type: Array,
      default: () => []
    },
    leafSelect: Boolean, // 叶子节点查询
    autoSearchRight: Boolean
  },
  data () {
    return {
      model: {},
      current: {},
      loading: false,
      current: null, // 当前节点
      collapse: false,
      treeData: [],
      defaultExpandedKeys: []
    }
  },
  methods: {
    onToolbarItemClick (action) {
      if (action && typeof action.click === 'function') {
        action.click(JSON.parse(JSON.stringify(this.current)))
      }
    },
    getToolbarItemIcon (action) {
      if (action.icon && typeof action.icon === 'function') {
        return action.icon(this.data)
      }
      return action.icon
    },
    getToolbarItemName (action) {
      if (action.label && typeof action.label === 'function') {
        return action.label(this.data)
      }
      return action.label
    },
    onCurrentNodeChange (data, node, leaf) {
      if (this.current && data[this.nodeKey] === this.current[this.nodeKey]) {
        this.current = data
        this.$emit('on-node-change', data, leaf)
        return
      }
      this.current = data
      this.$emit('on-node-change', data, leaf)
      if ((!leaf || !this.leafSelect || (this.leafSelect && leaf.isLeaf)) && this.autoSearchRight) {
        this.$nextTick(() => {
          this.select(true)
        })
      }
    },
    onToggleClick () {
      if (this.loading) return
      this.collapse = !this.collapse
    },
    getLeaf (values, id) {
      if (!values) return null
      for (let i = 0; i < values.length; ++i) {
        let val = values[i]
        if (val.children && val.children.length) {
          let leaf = this.getLeaf(val.children, id)
          if (leaf) return leaf
        } else {
          if (id !== undefined && val.id == id) {
            return val
          } else if (id === undefined || id === null) return val
        }
      }
      return null
    },
    select (reload) {
      this.$nextTick(() => {
        this.$refs.dataGrid.select(reload)
      })
    },
    selectTreeData (id) {
      if (!this.treeApi) return
      this.loading = true
      this.$api(this.treeApi, this.treeApiParams).then(res => {
        if (res) {
          this.treeData = res
          if (!res.length) {
            this.loading = false
            this.$refs.dataGrid.hideLoading()
            return
          }
          // 选取第一个叶子节点
          let currentId = ''
          let leaf = null
          if (!this.current && !id) {
            leaf = this.getLeaf(res)
            currentId = leaf ? leaf[this.nodeKey] : ''
          } else if (id) {
            currentId = id
            leaf = this.getLeaf(res, id)
          } else { // 重新获取刷新当前节点数据
            this.current = this.getLeaf(res, this.current[this.nodeKey])
            currentId = this.current[this.nodeKey]
          }
          // this.defaultExpandedKeys = [currentId]
          this.defaultExpandedKeys = [currentId]
          // 这一段可以改变树的选中项和右侧相应视图
          // currentId控制左侧
          // leaf控制右侧
          this.$nextTick(() => {
            this.$refs.tree.setCurrentKey(currentId)
            const $currentNode = this.$refs.tree.getCurrentNode()
            if (leaf) {
              this.onCurrentNodeChange(leaf, $currentNode, $currentNode)
            }
          })
          this.init = true
        }
        this.loading = false
      }).catch(e => {
        this.loading = false
      })
    },
    onNodeDropSuccess (prev, next, type, e, currentNodes) {
      this.$emit('on-node-drop-success', prev, next, type, e, currentNodes)
      this.$nextTick(() => {
        if (this.current && this.current[this.nodeKey]) this.$refs.tree.setCurrentKey(this.current[this.nodeKey])
      })
    },
    _toolbarItemShow (toolbar = null) { // 菜单项是否显示
      if (!toolbar) return true
      else if (typeof toolbar === 'boolean') {
        return toolbar
      } else if (typeof toolbar === 'function') {
        return toolbar(this.current)
      }
      return true
    }
  },
  mounted () {
    this.bindResponsiveObserver()
    if (this.curNodex != '') {
      this.selectTreeData(this.curNodex)
    } else {
      this.selectTreeData()
    }
  },
  beforeDestroy () {
    this.unbindResponsiveObserver()
  }
}
</script>

<style lang="less" scoped>
@import '../color.less';
.tree-data-grid {
    width: 100%;
    height: 100%;
    overflow: visible;
    white-space: nowrap;
    &-aside {
        vertical-align: text-top;
        display: inline-block;
        width: 300px;
        height: 100%;
        // flex: 0 0 300px;
        opacity: 1;
        transition: all .3s;
        padding: 12px;
        background: #fff;
        border-radius: 4px;
        &.collapse {
            opacity: 0;
            transform: translateX(-100%);
        }
        &-toolbar {
            height: 24px;
            display: flex;
            align-items: center;
            margin-bottom: 6px;
            .toolbar-item {
                margin-right: 12px;
                cursor: pointer;
                &:hover {
                    color: @primary;
                }
                &:last-child {
                    margin-right: 0px;
                }
            }
        }
        &:not(.dis-hover):hover {
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
            border-color: #eee;
        }
    }
    &-content {
        // flex: 0 0 calc(~'100% - 300px');
        width: calc(~'100% - 304px');
        vertical-align: text-top;
        display: inline-flex;
        height: 100%;
        transition: all .3s;
        &__trigger {
            position: relative;
            width: 12px;
            margin: 0 2px;
            background: transparent;
            cursor: pointer;
            border-radius: 8px;
            &:hover {
                background: rgba(0,0,0,.075);
            }
            .icon {
                display: inline-block;
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                color: #fff;
                font-weight: bold;
                transition: all .3s;
            }
            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 0px;
                width: 12px;
                height: 30px;
                transform: translateY(-50%);
                color: #fff;
                text-align: center;
                background: @primary;
                border-radius: 6px;
            }
        }
        &.full {
            // flex: 0 0 calc(~'100% - 0px');
            width: calc(~'100% - 0px');
            transform: translateX(-300px);
            .tree-data-grid-content__trigger {
                .icon {
                    transform: translateY(-50%) rotateY(180deg);
                }
            }
        }
        &__table {
            height:100%;
            width:calc(100% - 16px);
        }
    }
    &.is-not-desktop {
        flex-direction: column;
        .tree-data-grid-aside {
            width: 100%;
            max-height: 300px;
            margin-bottom: 12px;
        }
        .tree-data-grid-content {
            flex: 0 0 100%;
            width: 100%;
            &__table {
                height: 100%;
                width: 100%;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/framework/data-grid/tree-data-grid.vue